import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  LinearProgress,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Alert,
  Chip,
  Stack,
} from "@mui/material";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import AutorenewOutlinedIcon from "@mui/icons-material/AutorenewOutlined";
import ExtensionOutlinedIcon from "@mui/icons-material/ExtensionOutlined";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import CloudOutlinedIcon from "@mui/icons-material/CloudOutlined";
import { useTheme } from "@emotion/react";
import TenantLayout from "../../layouts/TenantLayout";
import Headeer from "../../components/Headeer";
import subscriptionService from "../../services/subscriptionService";
import { useNavigate, useLocation } from "react-router-dom";
import { tokens } from "../../theme";
import { useTranslation } from "react-i18next";

function bytesToGb(bytes) {
  return Number(bytes) / 1024 / 1024 / 1024;
}

function formatMoney(v) {
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return `₺${n.toFixed(2)}`;
}

function planLabel(p) {
  if (!p) return "—";
  return `${p.name} (${p.code})`;
}

/** 0–100, limit 0 ise 0 */
function usagePercent(used, limit) {
  const u = Number(used) || 0;
  const l = Number(limit);
  if (!Number.isFinite(l) || l <= 0) return 0;
  return Math.min(100, Math.round((u / l) * 1000) / 10);
}

export default function TenantSubscription() {
  const { t, i18n } = useTranslation("subscription");
  const dateLocale = useMemo(() => {
    const base = i18n.language?.split("-")[0];
    if (base === "tr") return "tr-TR";
    if (base === "fr") return "fr-FR";
    return "en-US";
  }, [i18n.language]);

  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const token = typeof window !== "undefined" ? localStorage.getItem("tokenTenant") : null;

  const [loading, setLoading] = useState(true);
  const [usage, setUsage] = useState(null);
  const [orders, setOrders] = useState([]);

  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [needsCheckout, setNeedsCheckout] = useState(false);

  const storageLimitGb = usage?.limits?.storage_limit_gb;
  const usedGb = bytesToGb(usage?.usage?.used_bytes || 0);
  const storageLimitBytes = useMemo(() => {
    if (usage?.limits?.storage_limit_bytes != null) return Number(usage.limits.storage_limit_bytes);
    const gb = Number(storageLimitGb);
    return Number.isFinite(gb) && gb > 0 ? gb * 1024 * 1024 * 1024 : 0;
  }, [usage?.limits?.storage_limit_bytes, storageLimitGb]);

  const refreshUsageAndOrders = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const current = await subscriptionService.getCurrent(token);
      if (current?.needs_checkout) {
        setNeedsCheckout(true);
        setUsage(null);
        setOrders([]);
        return;
      }
      setNeedsCheckout(false);
      const [usageRes, ordersRes] = await Promise.all([
        subscriptionService.getUsage(token),
        subscriptionService.getOrders(token),
      ]);

      setUsage(usageRes || null);
      setOrders(ordersRes?.orders || []);
    } catch (e) {
      setSnackbar({
        open: true,
        message: e?.response?.data?.error || e.message || t("errors.subscriptionInfoFailed"),
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshUsageAndOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const msg = location.state?.snackbarMessage;
    if (msg) {
      setSnackbar({ open: true, message: msg, severity: "success" });
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  const doctorBreakdownRows = usage?.doctor_breakdown?.doctors || [];
  const tenantInvitesThisMonth = usage?.doctor_breakdown?.tenant_invites_count_this_month || 0;

  const sub = usage?.subscription;

  const tenantCardSx = useMemo(
    () => ({
      borderRadius: 4,
      background: `linear-gradient(165deg, ${colors.primary[500]}f2 0%, ${colors.primary[600]}e8 100%)`,
      color: colors.grey[100],
      border: `1px solid ${colors.primary[300]}55`,
      boxShadow: `0 24px 48px rgba(15, 23, 42, 0.35)`,
    }),
    [colors]
  );

  const tableSx = useMemo(
    () => ({
      borderCollapse: "separate",
      "& .MuiTableHead-root .MuiTableCell-root": {
        backgroundColor: `${colors.blueAccent[700]}90`,
        color: colors.grey[100],
        fontWeight: 700,
        borderBottom: `1px solid ${colors.primary[400]}66`,
        fontSize: "0.8rem",
        letterSpacing: 0.3,
      },
      "& .MuiTableBody-root .MuiTableCell-root": {
        color: colors.grey[100],
        borderColor: `${colors.primary[400]}44`,
      },
      "& .MuiTableRow-root:hover": {
        backgroundColor: `${colors.blueAccent[800]}22`,
      },
    }),
    [colors]
  );

  const chipOutlineSx = {
    color: colors.grey[100],
    borderColor: `${colors.blueAccent[400]}66`,
    backgroundColor: `${colors.primary[700]}44`,
    fontWeight: 600,
  };

  const chipSolidSx = {
    fontWeight: 700,
    background: `linear-gradient(135deg, ${colors.blueAccent[600]} 0%, ${colors.greenAccent[500]} 100%)`,
    color: colors.grey[100],
    border: `1px solid ${colors.greenAccent[400]}55`,
  };

  return (
    <TenantLayout
      contentSx={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
        backgroundColor: "#f5f7fb",
        px: { xs: 2, md: 3 },
        py: { xs: 3, md: 4 },
      }}
    >
      <Box sx={{ width: "100%", maxWidth: 1200, margin: "0 auto" }}>
        <Headeer
          title={t("title")}
          subtitle={t("subtitle")}
          onLightBackground
        />

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
            <CircularProgress sx={{ color: colors.greenAccent[500] }} size={48} />
          </Box>
        ) : needsCheckout ? (
          <Alert
            severity="warning"
            sx={{
              borderRadius: 3,
              alignItems: "center",
              background: `linear-gradient(135deg, ${colors.blueAccent[700]}55 0%, ${colors.primary[500]}ee 100%)`,
              border: `1px solid ${colors.greenAccent[500]}55`,
              color: colors.grey[100],
              "& .MuiAlert-icon": { color: colors.greenAccent[400] },
            }}
          >
            Kurum paket ataması super admin tarafından yapılır. Lütfen yönetici ile iletişime geçin.
          </Alert>
        ) : !usage ? (
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 4,
              ...tenantCardSx,
            }}
          >
            <Typography sx={{ color: colors.redAccent[400], fontWeight: 600 }}>
              {t("noActiveSubscription")}
            </Typography>
          </Paper>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card sx={tenantCardSx}>
                <CardContent sx={{ py: 3 }}>
                  <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2.5 }}>
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: `linear-gradient(135deg, ${colors.blueAccent[600]} 0%, ${colors.greenAccent[500]} 100%)`,
                        boxShadow: `0 8px 20px rgba(15,23,42,0.35)`,
                      }}
                    >
                      <WorkspacePremiumOutlinedIcon sx={{ color: colors.grey[100], fontSize: 26 }} />
                    </Box>
                    <Box>
                      <Typography
                        variant="overline"
                        sx={{ color: colors.grey[400], letterSpacing: 2, fontWeight: 700, lineHeight: 1.2 }}
                      >
                        {t("overview.overline")}
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 800, color: colors.grey[100], letterSpacing: 0.2 }}>
                        {t("overview.heading")}
                      </Typography>
                    </Box>
                  </Stack>

                  {/* Paket kimliği */}
                  <Box
                    sx={{
                      p: 2.5,
                      mb: 2,
                      borderRadius: 3,
                      background: `linear-gradient(135deg, ${colors.blueAccent[800]}55 0%, ${colors.primary[600]}cc 55%, ${colors.primary[700]}99 100%)`,
                      border: `1px solid ${colors.blueAccent[500]}44`,
                      boxShadow: `inset 0 1px 0 ${colors.greenAccent[500]}18`,
                    }}
                  >
                    <Typography variant="caption" sx={{ color: colors.grey[400], fontWeight: 700, letterSpacing: 1.2 }}>
                      {t("package.label")}
                    </Typography>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      alignItems={{ xs: "flex-start", sm: "center" }}
                      justifyContent="space-between"
                      spacing={1.5}
                      sx={{ mt: 1 }}
                    >
                      <Box>
                        <Typography variant="h5" sx={{ fontWeight: 800, color: colors.grey[100], lineHeight: 1.25 }}>
                          {usage?.subscription?.Plan?.name || "—"}
                        </Typography>
                        <Chip
                          size="small"
                          label={usage?.subscription?.Plan?.code || "—"}
                          sx={{
                            mt: 1,
                            fontWeight: 800,
                            letterSpacing: 0.5,
                            backgroundColor: `${colors.primary[500]}99`,
                            color: colors.greenAccent[300],
                            border: `1px solid ${colors.greenAccent[500]}55`,
                          }}
                        />
                      </Box>
                      <Stack direction="row" gap={1} flexWrap="wrap" justifyContent={{ xs: "flex-start", sm: "flex-end" }}>
                        <Chip
                          size="small"
                          icon={
                            <AutorenewOutlinedIcon sx={{ fontSize: "18px !important", color: `${colors.grey[100]} !important` }} />
                          }
                          label={sub?.renewal_type === "yearly" ? t("package.yearlyBilling") : t("package.monthlyBilling")}
                          sx={chipSolidSx}
                        />
                        <Chip
                          size="small"
                          label={t("package.refMonthly", { amount: formatMoney(sub?.total_monthly_price_tl_snapshot) })}
                          sx={chipOutlineSx}
                        />
                      </Stack>
                    </Stack>

                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mt: 2.5 }}>
                      <Box
                        sx={{
                          flex: 1,
                          display: "flex",
                          gap: 1.5,
                          p: 1.75,
                          borderRadius: 2,
                          backgroundColor: `${colors.primary[700]}66`,
                          border: `1px solid ${colors.blueAccent[600]}33`,
                        }}
                      >
                        <EventAvailableOutlinedIcon sx={{ color: colors.blueAccent[300], fontSize: 28, flexShrink: 0 }} />
                        <Box>
                          <Typography variant="caption" sx={{ color: colors.grey[400], fontWeight: 600 }}>
                            {t("package.start")}
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 800, color: colors.grey[100] }}>
                            {usage?.subscription?.starts_at
                              ? new Date(usage.subscription.starts_at).toLocaleDateString(dateLocale)
                              : "—"}
                          </Typography>
                        </Box>
                      </Box>
                      <Box
                        sx={{
                          flex: 1,
                          display: "flex",
                          gap: 1.5,
                          p: 1.75,
                          borderRadius: 2,
                          backgroundColor: `${colors.primary[700]}66`,
                          border: `1px solid ${colors.greenAccent[600]}33`,
                        }}
                      >
                        <AutorenewOutlinedIcon sx={{ color: colors.greenAccent[400], fontSize: 28, flexShrink: 0 }} />
                        <Box>
                          <Typography variant="caption" sx={{ color: colors.grey[400], fontWeight: 600 }}>
                            {t("package.nextRenewal")}
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 800, color: colors.grey[100] }}>
                            {usage?.subscription?.next_renewal_at
                              ? new Date(usage.subscription.next_renewal_at).toLocaleDateString(dateLocale)
                              : "—"}
                          </Typography>
                        </Box>
                      </Box>
                    </Stack>
                  </Box>

                  {/* Ek kapasite */}
                  <Box
                    sx={{
                      p: 2,
                      mb: 2.5,
                      borderRadius: 2,
                      background: `linear-gradient(90deg, ${colors.greenAccent[700]}22 0%, ${colors.primary[700]}88 100%)`,
                      border: `1px dashed ${colors.greenAccent[500]}44`,
                    }}
                  >
                    <Stack direction="row" alignItems="flex-start" spacing={1.5}>
                      <ExtensionOutlinedIcon sx={{ color: colors.greenAccent[400], fontSize: 28, mt: 0.25 }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" sx={{ color: colors.grey[400], fontWeight: 700, letterSpacing: 0.8 }}>
                          {t("addon.title")}
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 800, color: colors.grey[100], mt: 0.5 }}>
                          {t("addon.doctorSlots", { count: usage?.subscription?.addon_doctor_count ?? 0 })}
                        </Typography>
                        <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mt: 1 }}>
                          <Chip
                            size="small"
                            label={t("addon.storageGb", { count: usage?.subscription?.addon_extra_storage_gb_total ?? 0 })}
                            sx={{
                              backgroundColor: `${colors.blueAccent[700]}44`,
                              color: colors.grey[200],
                              fontWeight: 600,
                              border: `1px solid ${colors.blueAccent[500]}44`,
                            }}
                          />
                          <Chip
                            size="small"
                            label={t("addon.emailPerMonth", { count: usage?.subscription?.addon_extra_email_monthly_total ?? 0 })}
                            sx={{
                              backgroundColor: `${colors.blueAccent[700]}44`,
                              color: colors.grey[200],
                              fontWeight: 600,
                              border: `1px solid ${colors.blueAccent[500]}44`,
                            }}
                          />
                        </Stack>
                      </Box>
                    </Stack>
                  </Box>

                  <Divider sx={{ borderColor: `${colors.primary[400]}55`, mb: 2.5 }} />

                  <Typography variant="caption" sx={{ color: colors.grey[400], fontWeight: 700, letterSpacing: 1, mb: 1.5, display: "block" }}>
                    {t("quotas.heading")}
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Box
                        sx={{
                          height: "100%",
                          p: 2,
                          borderRadius: 2.5,
                          background: `linear-gradient(160deg, ${colors.primary[700]}90 0%, ${colors.primary[800]}60 100%)`,
                          border: `1px solid ${colors.blueAccent[500]}35`,
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                          <PeopleOutlineIcon sx={{ color: colors.greenAccent[400], fontSize: 22 }} />
                          <Typography variant="caption" sx={{ color: colors.grey[400], fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6 }}>
                            {t("quotas.doctors")}
                          </Typography>
                        </Stack>
                        <Typography variant="h4" sx={{ fontWeight: 800, color: colors.grey[100] }}>
                          {usage?.usage?.doctors_count}
                          <Typography component="span" variant="h6" sx={{ color: colors.grey[500], fontWeight: 600 }}>
                            {" "}
                            / {usage?.limits?.doctor_limit}
                          </Typography>
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={usagePercent(usage?.usage?.doctors_count, usage?.limits?.doctor_limit)}
                          sx={{
                            mt: 2,
                            height: 10,
                            borderRadius: 5,
                            bgcolor: `${colors.primary[500]}99`,
                            "& .MuiLinearProgress-bar": {
                              borderRadius: 5,
                              background: `linear-gradient(90deg, ${colors.greenAccent[500]} 0%, ${colors.blueAccent[500]} 100%)`,
                            },
                          }}
                        />
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Box
                        sx={{
                          height: "100%",
                          p: 2,
                          borderRadius: 2.5,
                          background: `linear-gradient(160deg, ${colors.primary[700]}90 0%, ${colors.primary[800]}60 100%)`,
                          border: `1px solid ${colors.blueAccent[500]}35`,
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                          <EmailOutlinedIcon sx={{ color: colors.blueAccent[300], fontSize: 22 }} />
                          <Typography variant="caption" sx={{ color: colors.grey[400], fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6 }}>
                            {t("quotas.emailMonth")}
                          </Typography>
                        </Stack>
                        <Typography variant="h4" sx={{ fontWeight: 800, color: colors.grey[100] }}>
                          {usage?.usage?.emails_sent_count}
                          <Typography component="span" variant="h6" sx={{ color: colors.grey[500], fontWeight: 600 }}>
                            {" "}
                            / {usage?.limits?.monthly_email_limit}
                          </Typography>
                        </Typography>
                        <Typography variant="body2" sx={{ color: colors.greenAccent[300], fontWeight: 600, mt: 0.5 }}>
                          {t("quotas.remaining", { count: usage?.derived?.emails_remaining_this_month })}
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={usagePercent(usage?.usage?.emails_sent_count, usage?.limits?.monthly_email_limit)}
                          sx={{
                            mt: 2,
                            height: 10,
                            borderRadius: 5,
                            bgcolor: `${colors.primary[500]}99`,
                            "& .MuiLinearProgress-bar": {
                              borderRadius: 5,
                              background: `linear-gradient(90deg, ${colors.blueAccent[400]} 0%, ${colors.greenAccent[400]} 100%)`,
                            },
                          }}
                        />
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Box
                        sx={{
                          height: "100%",
                          p: 2,
                          borderRadius: 2.5,
                          background: `linear-gradient(160deg, ${colors.primary[700]}90 0%, ${colors.primary[800]}60 100%)`,
                          border: `1px solid ${colors.blueAccent[500]}35`,
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                          <CloudOutlinedIcon sx={{ color: colors.blueAccent[200], fontSize: 22 }} />
                          <Typography variant="caption" sx={{ color: colors.grey[400], fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6 }}>
                            {t("quotas.storage")}
                          </Typography>
                        </Stack>
                        <Typography variant="h5" sx={{ fontWeight: 800, color: colors.grey[100] }}>
                          {usedGb.toFixed(2)} GB
                          <Typography component="span" variant="body1" sx={{ color: colors.grey[500], fontWeight: 600 }}>
                            {" "}
                            / {storageLimitGb} GB
                          </Typography>
                        </Typography>
                        <Typography variant="body2" sx={{ color: colors.greenAccent[300], fontWeight: 600, mt: 0.5 }}>
                          {t("quotas.remainingGb", {
                            amount: bytesToGb(usage?.derived?.storage_remaining_bytes || 0).toFixed(2),
                          })}
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={usagePercent(usage?.usage?.used_bytes, storageLimitBytes)}
                          sx={{
                            mt: 2,
                            height: 10,
                            borderRadius: 5,
                            bgcolor: `${colors.primary[500]}99`,
                            "& .MuiLinearProgress-bar": {
                              borderRadius: 5,
                              background: `linear-gradient(90deg, ${colors.blueAccent[300]} 0%, ${colors.greenAccent[500]} 100%)`,
                            },
                          }}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card sx={{ ...tenantCardSx, mt: 0 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: colors.grey[100], mb: 1, letterSpacing: 0.3 }}>
                    {t("doctorUsage.title")}
                  </Typography>
                  <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                    {t("doctorUsage.tenantInvites", { count: tenantInvitesThisMonth })}
                  </Typography>

                  <Box sx={{ overflowX: "auto", mt: 2 }}>
                    <Table size="small" sx={tableSx}>
                      <TableHead>
                        <TableRow>
                          <TableCell>{t("table.doctor")}</TableCell>
                          <TableCell>{t("table.role")}</TableCell>
                          <TableCell align="right">{t("table.email")}</TableCell>
                          <TableCell align="right">{t("table.patient")}</TableCell>
                          <TableCell align="right">{t("table.storageNet")}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {doctorBreakdownRows.map((row) => (
                          <TableRow key={row.id}>
                            <TableCell>
                              <Typography sx={{ fontWeight: 700, color: colors.grey[100] }}>
                                {row.name} {row.surname}
                              </Typography>
                              <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                                {row.email}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ color: colors.grey[200] }}>{row.role}</TableCell>
                            <TableCell align="right">{row.emails_sent_count_this_month}</TableCell>
                            <TableCell align="right">{row.patients_count}</TableCell>
                            <TableCell align="right">{bytesToGb(row.storage_used_bytes_net).toFixed(2)} GB</TableCell>
                          </TableRow>
                        ))}
                        {doctorBreakdownRows.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={5}>
                              <Typography sx={{ color: colors.grey[400] }}>{t("table.noDoctors")}</Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card sx={tenantCardSx}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: colors.grey[100], mb: 2, letterSpacing: 0.3 }}>
                    {t("history.title")}
                  </Typography>
                  <Box sx={{ overflowX: "auto" }}>
                    <Table size="small" sx={tableSx}>
                      <TableHead>
                        <TableRow>
                          <TableCell>{t("history.action")}</TableCell>
                          <TableCell>{t("history.beforeAfter")}</TableCell>
                          <TableCell align="right">{t("history.addonSlot")}</TableCell>
                          <TableCell align="right">{t("history.refMonthly")}</TableCell>
                          <TableCell align="right">{t("history.charge")}</TableCell>
                          <TableCell>{t("history.period")}</TableCell>
                          <TableCell>{t("history.source")}</TableCell>
                          <TableCell>{t("history.date")}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {orders.map((o) => (
                          <TableRow key={o.id}>
                            <TableCell sx={{ color: colors.grey[100], fontWeight: 600 }}>
                              {t(`orderTypes.${o.type}`, { defaultValue: o.type })}
                            </TableCell>
                            <TableCell sx={{ color: colors.grey[100], maxWidth: 220 }}>
                              <Typography variant="body2" noWrap title={planLabel(o.old_plan)}>
                                {planLabel(o.old_plan)}
                              </Typography>
                              <Typography variant="caption" sx={{ color: colors.grey[500] }}>
                                →
                              </Typography>
                              <Typography variant="body2" noWrap title={planLabel(o.new_plan)}>
                                {planLabel(o.new_plan)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              {o.old_addon_doctor_count ?? "—"} → {o.new_addon_doctor_count ?? "—"}
                            </TableCell>
                            <TableCell align="right" sx={{ color: colors.grey[400] }}>
                              {formatMoney(o.monthly_total_tl_snapshot)}
                            </TableCell>
                            <TableCell align="right" sx={{ color: colors.grey[100], fontWeight: 700 }}>
                              {formatMoney(o.amount_tl)}
                            </TableCell>
                            <TableCell sx={{ color: colors.grey[400] }}>
                              {o.billing_cycle_snapshot === "yearly"
                                ? t("history.billingYearly")
                                : o.billing_cycle_snapshot === "monthly"
                                  ? t("history.billingMonthly")
                                  : "—"}
                            </TableCell>
                            <TableCell sx={{ color: colors.grey[400] }}>{o.provider || "—"}</TableCell>
                            <TableCell sx={{ color: colors.grey[400] }}>
                              {o.paid_at
                                ? new Date(o.paid_at).toLocaleString(dateLocale, {
                                    dateStyle: "short",
                                    timeStyle: "short",
                                  })
                                : "—"}
                            </TableCell>
                          </TableRow>
                        ))}
                        {orders.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={8}>
                              <Typography sx={{ color: colors.grey[400] }}>{t("history.noOrders")}</Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        <Snackbar
          open={snackbar.open}
          autoHideDuration={3500}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
            severity={snackbar.severity}
            variant="filled"
            sx={{
              borderRadius: 2,
              fontWeight: 600,
              ...(snackbar.severity === "success"
                ? {
                    background: `linear-gradient(135deg, ${colors.greenAccent[600]} 0%, ${colors.blueAccent[600]} 100%)`,
                    color: colors.grey[100],
                  }
                : {
                    background: `${colors.redAccent[600]}ee`,
                    color: colors.grey[100],
                  }),
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </TenantLayout>
  );
}
